import React from 'react';
import { FeaturesSlider as LatitudeFeaturesSlider } from '@latitude/features-slider';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { Link } from '@latitude/link';
import { Text } from '@latitude/text';
import { ALIGN, MARGIN } from '@latitude/core/utils/constants';
import { getCdrEntryBindingRichTextOptions } from '@/utils/contentfulRichTextBindings';

const LINK_ITEM_CONTENT_TYPE = 'menuItems';

const getContentTypeId = entry =>
  entry?.contentType?.sys?.id ||
  entry?.contentType ||
  entry?.sys?.contentType?.sys?.id;

const renderInlineLinkEntry = (node, children, location) => {
  const target = node?.data?.target;

  if (getContentTypeId(target) !== LINK_ITEM_CONTENT_TYPE) return null;

  const label = target?.label || target?.title;
  const linkChildren = Array.isArray(children)
    ? children.some(Boolean) && children
    : children || label;

  if (!target?.link) return linkChildren || label || null;

  return (
    <Link
      href={target.link}
      trackEventData={{
        category: 'text-link',
        action: 'internal-link',
        location: target?.location || location,
        label: target?.trackEventData || label
      }}
    >
      {linkChildren || label}
    </Link>
  );
};

const getCardRichTextOptions = location =>
  getCdrEntryBindingRichTextOptions({
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_node, children) => <>{children}</>,
      [INLINES.HYPERLINK]: (node, children) => (
        <Link
          href={node?.data?.uri}
          trackEventData={{
            category: 'text-link',
            action: 'internal-link',
            location,
            label: children
          }}
        >
          {children}
        </Link>
      ),
      [INLINES.EMBEDDED_ENTRY]: (node, children) =>
        renderInlineLinkEntry(node, children, location)
    }
  });

const renderCardRichText = (card, contentAlign) => (
  <Text align={contentAlign} marginBottom={MARGIN.M16}>
    {documentToReactComponents(
      card.richText,
      getCardRichTextOptions(card.title)
    )}
  </Text>
);

const normalizeCardRichText = (data, contentAlign) =>
  data?.map(card => {
    if (!card?.richText) return card;

    const richTextContent = renderCardRichText(card, contentAlign);

    return {
      ...card,
      text: '',
      jsx: card.jsx ? (
        <>
          {richTextContent}
          {card.jsx}
        </>
      ) : (
        richTextContent
      )
    };
  }) || [];

const FeaturesSlider = ({
  data = [],
  contentAlign = ALIGN.CENTER,
  ...props
}) => (
  <LatitudeFeaturesSlider
    {...props}
    contentAlign={contentAlign}
    data={normalizeCardRichText(data, contentAlign)}
  />
);

export default FeaturesSlider;
